import ArchiveIcon from '@mui/icons-material/Archive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  type FormSummary,
  type ListSubmissionsParams,
  type OnboardingSubmission,
  type PullAllResult,
  useBackfillAllIdentityMutation,
  useListDistinctCompaniesQuery,
  useListOnboardingFormsQuery,
  useListOnboardingSubmissionsQuery,
  usePullAllResponsesMutation,
  usePullResponsesMutation,
  useReclassifyFormTypeMutation,
} from './api/onboardingApi';
import { downloadAdminCsv } from './utils/downloadAdminCsv';

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

/** Top-level page tabs. */
const PAGE_TABS = ['submissions', 'forms'] as const;
type PageTab = (typeof PAGE_TABS)[number];

function parsePageTab(value: string | null): PageTab {
  if (value === 'forms') return 'forms';
  return 'submissions';
}

// ---------------------------------------------------------------------------
// Submissions tab constants
// ---------------------------------------------------------------------------

const FORM_TYPE_TABS = [
  { label: 'All', value: '' },
  { label: 'Member Onboarding', value: 'member_onboarding' },
  { label: 'Pulse Check', value: 'pulse_check' },
  { label: 'Unrecognized', value: 'unrecognized' },
] as const;

const STATUS_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Active', value: 'active' },
  { label: 'Archived', value: 'archived' },
] as const;

type SortField =
  | 'submitted_at'
  | 'respondent_email'
  | 'respondent_first_name'
  | 'respondent_company';
type SortDir = 'asc' | 'desc';

const VALID_SORT_FIELDS: SortField[] = [
  'submitted_at',
  'respondent_email',
  'respondent_first_name',
  'respondent_company',
];
const VALID_SORT_DIRS: SortDir[] = ['asc', 'desc'];

function parseSortField(value: string | null): SortField {
  return VALID_SORT_FIELDS.includes(value as SortField) ? (value as SortField) : 'submitted_at';
}

function parseSortDir(value: string | null): SortDir {
  return VALID_SORT_DIRS.includes(value as SortDir) ? (value as SortDir) : 'desc';
}

function parsePage(value: string | null): number {
  const num = Number.parseInt(value ?? '1', 10);
  return Number.isNaN(num) || num < 1 ? 1 : num;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function respondentName(sub: OnboardingSubmission): string {
  const parts = [sub.respondent_first_name, sub.respondent_last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : '\u2014';
}

// ---------------------------------------------------------------------------
// Forms tab constants
// ---------------------------------------------------------------------------

const KNOWN_FORM_TYPES = ['member_onboarding', 'pulse_check', 'exit_survey', 'coach_feedback'];

// ---------------------------------------------------------------------------
// SubmissionsTab
// ---------------------------------------------------------------------------

function SubmissionsTab() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [formType, setFormTypeRaw] = useState(searchParams.get('form_type') ?? '');
  const [status, setStatusRaw] = useState(searchParams.get('status') ?? 'active');
  const [company, setCompanyRaw] = useState(searchParams.get('company') ?? '');
  const [searchText, setSearchText] = useState(searchParams.get('q') ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchText);
  const [page, setPage] = useState(parsePage(searchParams.get('page')));

  const setFormType = useCallback((v: string) => {
    setFormTypeRaw(v);
    setPage(1);
  }, []);
  const setStatus = useCallback((v: string) => {
    setStatusRaw(v);
    setPage(1);
  }, []);
  const setCompany = useCallback((v: string) => {
    setCompanyRaw(v);
    setPage(1);
  }, []);
  const [sortBy, setSortBy] = useState<SortField>(parseSortField(searchParams.get('sort_by')));
  const [sortDir, setSortDir] = useState<SortDir>(parseSortDir(searchParams.get('sort_dir')));
  const [exportError, setExportError] = useState<string | null>(null);

  const perPage = 25;

  const prevDebouncedRef = useRef(debouncedSearch);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  if (prevDebouncedRef.current !== debouncedSearch) {
    prevDebouncedRef.current = debouncedSearch;
    if (page !== 1) setPage(1);
  }

  const queryParams = useMemo<ListSubmissionsParams>(() => {
    const params: ListSubmissionsParams = {
      page,
      per: perPage,
      sort_by: sortBy,
      sort_dir: sortDir,
    };
    if (formType) params.form_type = formType;
    if (status) params.status = status;
    if (company) params.company = company;
    if (debouncedSearch) params.q = debouncedSearch;
    return params;
  }, [page, sortBy, sortDir, formType, status, company, debouncedSearch]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    // Preserve the tab param
    params.set('tab', 'submissions');
    if (formType) {
      params.set('form_type', formType);
    } else {
      params.delete('form_type');
    }
    if (status) {
      params.set('status', status);
    } else {
      params.delete('status');
    }
    if (company) {
      params.set('company', company);
    } else {
      params.delete('company');
    }
    if (debouncedSearch) {
      params.set('q', debouncedSearch);
    } else {
      params.delete('q');
    }
    if (page > 1) {
      params.set('page', String(page));
    } else {
      params.delete('page');
    }
    if (sortBy !== 'submitted_at') {
      params.set('sort_by', sortBy);
    } else {
      params.delete('sort_by');
    }
    if (sortDir !== 'desc') {
      params.set('sort_dir', sortDir);
    } else {
      params.delete('sort_dir');
    }
    setSearchParams(params, { replace: true });
  }, [
    formType,
    status,
    company,
    debouncedSearch,
    page,
    sortBy,
    sortDir,
    searchParams,
    setSearchParams,
  ]);

  const { data, isLoading, error } = useListOnboardingSubmissionsQuery(queryParams);
  const { data: companiesData } = useListDistinctCompaniesQuery();

  const handleSort = useCallback(
    (field: SortField) => {
      if (sortBy === field) {
        setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(field);
        setSortDir(field === 'submitted_at' ? 'desc' : 'asc');
      }
    },
    [sortBy]
  );

  const handleExport = useCallback(async () => {
    setExportError(null);
    try {
      const params = new URLSearchParams();
      if (formType) params.set('form_type', formType);
      if (status) params.set('status', status);
      if (company) params.set('company', company);
      if (debouncedSearch) params.set('q', debouncedSearch);
      await downloadAdminCsv({
        target: 'admin.onboarding.submissions_csv',
        params: Object.fromEntries(params.entries()),
      });
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    }
  }, [formType, status, company, debouncedSearch]);

  const submissions = data?.submissions ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / perPage);

  const activeTabIndex = FORM_TYPE_TABS.findIndex((t) => t.value === formType);
  const tabIndex = activeTabIndex >= 0 ? activeTabIndex : 0;

  // Use distinct companies from dedicated endpoint (not limited to current page)
  const companies = companiesData?.companies ?? [];

  if (isLoading && !data) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load submissions</Alert>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          {total} total submission{total !== 1 ? 's' : ''} matching current filters
        </Typography>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>
          Export CSV
        </Button>
      </Box>

      {exportError && (
        <Alert severity="error" onClose={() => setExportError(null)} sx={{ mb: 2 }}>
          {exportError}
        </Alert>
      )}

      {/* Form type sub-tabs */}
      <Tabs
        value={tabIndex}
        onChange={(_, newIndex) => setFormType(FORM_TYPE_TABS[newIndex].value)}
        sx={{ mb: 2 }}
      >
        {FORM_TYPE_TABS.map((tab) => (
          <Tab key={tab.value} label={tab.label} />
        ))}
      </Tabs>

      {/* Filters row */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          label="Search"
          placeholder="Name, email, company..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Company</InputLabel>
          <Select value={company} label="Company" onChange={(e) => setCompany(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {companies.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Submissions table */}
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'respondent_first_name'}
                  direction={sortBy === 'respondent_first_name' ? sortDir : 'asc'}
                  onClick={() => handleSort('respondent_first_name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'respondent_email'}
                  direction={sortBy === 'respondent_email' ? sortDir : 'asc'}
                  onClick={() => handleSort('respondent_email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'respondent_company'}
                  direction={sortBy === 'respondent_company' ? sortDir : 'asc'}
                  onClick={() => handleSort('respondent_company')}
                >
                  Company
                </TableSortLabel>
              </TableCell>
              <TableCell>Form Type</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'submitted_at'}
                  direction={sortBy === 'submitted_at' ? sortDir : 'desc'}
                  onClick={() => handleSort('submitted_at')}
                >
                  Submitted
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Linked</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {submissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No submissions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              submissions.map((sub) => {
                const isUnrecognized = sub.form_type.startsWith('unrecognized:');
                const missingEmail = !sub.respondent_email;
                return (
                  <TableRow
                    key={sub.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/typeform/${sub.id}`)}
                  >
                    <TableCell>{respondentName(sub)}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {missingEmail && (
                          <WarningIcon
                            color="warning"
                            fontSize="small"
                            titleAccess="Missing email"
                          />
                        )}
                        {sub.respondent_email ?? '\u2014'}
                      </Box>
                    </TableCell>
                    <TableCell>{sub.respondent_company ?? '\u2014'}</TableCell>
                    <TableCell>
                      {isUnrecognized ? (
                        <Chip
                          icon={<WarningIcon />}
                          label={sub.form_type}
                          color="error"
                          size="small"
                        />
                      ) : (
                        <Chip label={sub.form_type} size="small" />
                      )}
                    </TableCell>
                    <TableCell>{formatDate(sub.submitted_at)}</TableCell>
                    <TableCell>
                      {sub.status === 'archived' ? (
                        <Chip
                          icon={<ArchiveIcon />}
                          label="Archived"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Active"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {sub.linked_person_id ? (
                        <LinkIcon color="success" fontSize="small" titleAccess="Linked" />
                      ) : (
                        '\u2014'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={2}>
          <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Typography variant="body2">
            Page {page} of {totalPages}
          </Typography>
          <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// FormsTab
// ---------------------------------------------------------------------------

function FormsTab() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useListOnboardingFormsQuery();
  const [reclassify] = useReclassifyFormTypeMutation();
  const [pullResponses] = usePullResponsesMutation();
  const [pullAll] = usePullAllResponsesMutation();
  const [backfillAll] = useBackfillAllIdentityMutation();

  const [reclassifyDialog, setReclassifyDialog] = useState<FormSummary | null>(null);
  const [selectedFormType, setSelectedFormType] = useState('');
  const [alertMessage, setAlertMessage] = useState<{
    severity: 'success' | 'error' | 'info';
    text: string;
  } | null>(null);
  const [pullingFormId, setPullingFormId] = useState<string | null>(null);
  const [pullingAll, setPullingAll] = useState(false);
  const [backfillingAll, setBackfillingAll] = useState(false);

  const handlePull = async (formId: string) => {
    setPullingFormId(formId);
    setAlertMessage(null);
    try {
      const result = await pullResponses({ formId }).unwrap();
      setAlertMessage({
        severity: 'success',
        text: `Pulled ${result.fetched} responses: ${result.stored} stored, ${result.duplicates} duplicates, ${result.failed} failed`,
      });
    } catch {
      setAlertMessage({ severity: 'error', text: `Failed to pull responses for ${formId}` });
    } finally {
      setPullingFormId(null);
    }
  };

  const handlePullAll = async () => {
    setPullingAll(true);
    setAlertMessage(null);
    try {
      const result: PullAllResult = await pullAll().unwrap();
      const { total } = result;
      setAlertMessage({
        severity: 'success',
        text: `Pulled all ${result.forms} forms: ${total.fetched} fetched, ${total.stored} stored, ${total.duplicates} duplicates, ${total.failed} failed`,
      });
    } catch {
      setAlertMessage({ severity: 'error', text: 'Failed to pull responses for all forms' });
    } finally {
      setPullingAll(false);
    }
  };

  const handleBackfillAll = async () => {
    setBackfillingAll(true);
    setAlertMessage(null);
    try {
      const result = await backfillAll().unwrap();
      setAlertMessage({
        severity: 'success',
        text: `Backfill complete: ${result.updated} updated, ${result.failed} failed out of ${result.total} total`,
      });
    } catch {
      setAlertMessage({
        severity: 'error',
        text: 'Failed to backfill identity for all submissions',
      });
    } finally {
      setBackfillingAll(false);
    }
  };

  const handleReclassify = async () => {
    if (!reclassifyDialog || !selectedFormType) return;
    try {
      const result = await reclassify({
        formId: reclassifyDialog.typeform_form_id,
        form_type: selectedFormType,
      }).unwrap();
      setAlertMessage({
        severity: 'info',
        text: `Reclassified ${result.reclassified} submissions, backfilled ${result.backfilled} identities`,
      });
      setReclassifyDialog(null);
      setSelectedFormType('');
    } catch {
      setAlertMessage({ severity: 'error', text: 'Failed to reclassify form type' });
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load forms</Alert>;
  }

  const forms = data?.forms ?? [];

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="body2" color="text.secondary">
          {forms.length} connected Typeform form{forms.length !== 1 ? 's' : ''}
        </Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" disabled={backfillingAll} onClick={handleBackfillAll}>
            {backfillingAll ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            Backfill All Identity
          </Button>
          <Button variant="contained" disabled={pullingAll} onClick={handlePullAll}>
            {pullingAll ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            Pull All Forms
          </Button>
        </Box>
      </Box>

      {alertMessage && (
        <Alert
          severity={alertMessage.severity}
          onClose={() => setAlertMessage(null)}
          sx={{ mb: 2 }}
        >
          {alertMessage.text}
        </Alert>
      )}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Form Title</TableCell>
              <TableCell>Form Type</TableCell>
              <TableCell>Form ID</TableCell>
              <TableCell align="right">Submissions</TableCell>
              <TableCell>Mappings</TableCell>
              <TableCell align="right">Unmapped</TableCell>
              <TableCell>Last Submission</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {forms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No forms have received submissions yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              forms.map((form) => {
                const isUnrecognized = form.form_type.startsWith('unrecognized:');
                return (
                  <TableRow
                    key={form.typeform_form_id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() =>
                      navigate(`/admin/typeform/forms/${form.typeform_form_id}/mappings`)
                    }
                  >
                    <TableCell>{form.form_title || '(untitled)'}</TableCell>
                    <TableCell>
                      {isUnrecognized ? (
                        <Chip
                          icon={<WarningIcon />}
                          label={form.form_type}
                          color="warning"
                          size="small"
                        />
                      ) : (
                        <Chip label={form.form_type} size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace">
                        {form.typeform_form_id}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{form.submission_count}</TableCell>
                    <TableCell>
                      {form.has_mappings ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <WarningIcon color="warning" fontSize="small" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {form.unmapped_count > 0 ? (
                        <Chip label={form.unmapped_count} color="warning" size="small" />
                      ) : (
                        form.unmapped_count
                      )}
                    </TableCell>
                    <TableCell>
                      {form.latest_submission_at
                        ? new Date(form.latest_submission_at).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={pullingFormId === form.typeform_form_id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePull(form.typeform_form_id);
                          }}
                        >
                          {pullingFormId === form.typeform_form_id ? (
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                          ) : null}
                          Pull
                        </Button>
                        {isUnrecognized && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={(e) => {
                              e.stopPropagation();
                              setReclassifyDialog(form);
                            }}
                          >
                            Reclassify
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Reclassify Dialog */}
      <Dialog open={!!reclassifyDialog} onClose={() => setReclassifyDialog(null)}>
        <DialogTitle>Reclassify Form Type</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Reclassify all submissions for form{' '}
            <strong>{reclassifyDialog?.typeform_form_id}</strong> from{' '}
            <strong>{reclassifyDialog?.form_type}</strong> to a new type. This will also re-extract
            identity fields using current mappings.
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>New Form Type</InputLabel>
            <Select
              value={selectedFormType}
              label="New Form Type"
              onChange={(e) => setSelectedFormType(e.target.value)}
            >
              {KNOWN_FORM_TYPES.map((ft) => (
                <MenuItem key={ft} value={ft}>
                  {ft}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReclassifyDialog(null)}>Cancel</Button>
          <Button onClick={handleReclassify} variant="contained" disabled={!selectedFormType}>
            Reclassify
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TypeformPage (main component)
// ---------------------------------------------------------------------------

export function TypeformPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parsePageTab(searchParams.get('tab'));

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: number) => {
      const tab = PAGE_TABS[newValue];
      const params = new URLSearchParams();
      if (tab !== 'submissions') params.set('tab', tab);
      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Typeform
      </Typography>

      <Tabs
        value={PAGE_TABS.indexOf(activeTab)}
        onChange={handleTabChange}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Submissions" />
        <Tab label="Forms" />
      </Tabs>

      {activeTab === 'submissions' && <SubmissionsTab />}
      {activeTab === 'forms' && <FormsTab />}
    </Box>
  );
}
